import { createFileRoute } from "@tanstack/react-router";
import { FilesIcon } from "lucide-react";
import { Suspense } from "react";
import { DocumentsLibrary } from "@/components/ai-documents/documents-library";
import { documentsPageOptions } from "@/hooks/use-document-store";
import { parseDocumentsSearch } from "@/lib/documents-filters";
import { ListSkeleton } from "@/ui/components/skeletons/page-skeletons";

export const Route = createFileRoute("/_workspace/documents")({
  /**
   * Starts the first page's request as the navigation begins, rather than
   * after this route's chunk has downloaded and the library has mounted — two
   * waits that used to run one after the other.
   *
   * Deliberately not awaited: the library renders its own toolbar and row
   * skeletons while the request is in flight, which is a better wait than a
   * blank route. The loader only needs to have *started* it, on the same query
   * key the component subscribes to.
   */
  loader: ({ context, location }) => {
    const { filters } = parseDocumentsSearch(new URLSearchParams(location.searchStr));
    void context.queryClient.ensureInfiniteQueryData(documentsPageOptions(filters)).catch(() => {
      // The library surfaces the failure itself; this is only a head start.
    });
  },
  component: DocumentsPage,
});

function DocumentsPage() {
  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 font-medium text-[10px] text-muted-foreground uppercase tracking-[0.25em]">
            <FilesIcon className="size-3.5" />
            Documents
          </p>
          <h1 className="font-bold text-2xl tracking-tight">Your documents</h1>
          <p className="text-muted-foreground text-sm">
            Drafts and finished documents, saved to this browser. Re-open, duplicate, download, or
            remove them.
          </p>
        </div>

        <Suspense fallback={<ListSkeleton rows={5} />}>
          <DocumentsLibrary />
        </Suspense>
      </div>
    </main>
  );
}
